import { AnimatedContentLoader, Page } from '@components'
import {
  useBusinessContext,
  FeatureWrapper,
  FlagWrapper,
  getBusinessHomePath,
  PermissionBoundary,
  PermissionSet,
  usePermission,
  usePermissionsLoaded
} from '@context'
import { BankIntegrationOnboardingPage } from '@pages/BankIntegrationOnboardingPage'
import {
  BusinessForms,
  isBusinessBillingLocked,
  fetchFeaturebaseJwt,
  trackBusinessPageVisit,
  triggerBankIntegrationBackgroundFetch,
  useCurrentUser
} from '@query'
import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Redirect, Route, Switch } from 'react-router-dom'
import { identifyFeaturebase, isFeaturebaseEnabled } from '@utils'
import { getDashboardPageUrl, getSettingsPageUrl } from '@constants/urls'

import AccountsPage from '@pages/AccountsPage'
import ContactsPage from '@pages/ContactsPage'
import DashboardPage from '@pages/DashboardPage'
import DocumentPage from '@pages/DocumentPage'
import FilesPage from '@pages/FilesPage'
import InvoicingPage from '@pages/InvoicingPage'
import PeriodPage from '@pages/PeriodPage'
import ProductPage from '@pages/ProductPage'
import ReportsPage from '@pages/ReportsPage'
import SettingsPage from '@pages/SettingsPage'
import VatPage from '@pages/VatPage'
import SalaryPage from '@pages/SalaryPage/SalaryPage'
import { ClaimSummaryPage } from '@pages/TravelAndExpensesPage/ClaimSummaryPage'
import { ExpenseEditorPage } from '@pages/TravelAndExpensesPage/ExpenseEditorPage'
import { TravelAndExpensesPage } from '@pages/TravelAndExpensesPage/TravelAndExpensesPage'
import { TripEditorPage } from '@pages/TravelAndExpensesPage/TripEditorPage'
import WorkflowsPage from '@pages/WorkflowsPage'
import NojaPage from '@pages/NojaPage'
import IfInsurancePage from '@pages/IfInsurancePage'
import { ImportJobProvider } from '@containers/CSVImportWizard/base/ImportJobContext'
import { ImportJobsTracker } from '@containers/CSVImportWizard/tracker/ImportJobsTracker'
import { BusinessAccountsPage } from '../BusinessAccountsPage'
import { BillingLockBanner } from '@root/containers/BillingLockBanner'
import { DemoBanner } from '@root/containers/DemoBanner/DemoBanner'
import { BillingLockGuard } from './BillingLockGuard'

interface BusinessPageProps {
  baseUrl: string
}

export const BusinessPage: React.FC<BusinessPageProps> = ({ baseUrl }) => {
  const { slug, data: business } = useBusinessContext()
  const featurebaseOrganization = import.meta.env.VITE_FEATUREBASE_ORGANIZATION

  const { data: user } = useCurrentUser()

  useEffect(() => {
    void (async () => {
      if (
        !isFeaturebaseEnabled() ||
        !user ||
        !user.tos_accepted ||
        !business ||
        !featurebaseOrganization
      )
        return

      try {
        const { token } = await fetchFeaturebaseJwt()
        identifyFeaturebase({
          organization: featurebaseOrganization,
          featurebaseJwt: token
        })
      } catch {
        // Keep business page behavior unchanged if Featurebase identify fails.
      }
    })()
  }, [business, featurebaseOrganization, user])

  if (business?.name) {
    window.document.title = 'NoCFO | ' + business?.name
  } else {
    window.document.title = 'NoCFO'
  }

  return (
    <ImportJobProvider>
      <BillingLockGuard baseUrl={baseUrl}>
        <Switch>
          <Route path={`${baseUrl}/bank-integration-flow`}>
            <BankIntegrationOnboardingPage baseUrl={`${baseUrl}/bank-integration-flow`} />
          </Route>
          <Route path={`${baseUrl}/business-accounts`}>
            <FeatureWrapper feature="holvi_business_accounts">
              <BusinessAccountsPage />
            </FeatureWrapper>
          </Route>
          <Route path={`${baseUrl}/salaries`}>
            <FeatureWrapper feature="salaries">
              <PermissionBoundary required={PermissionSet.SALARY}>
                <SalaryPage />
              </PermissionBoundary>
            </FeatureWrapper>
          </Route>
          <Route path={`${baseUrl}/noja`}>
            <FeatureWrapper feature="noja">
              <NojaPage />
            </FeatureWrapper>
          </Route>
          <Route path={`${baseUrl}/if-insurance`}>
            <FeatureWrapper feature="if_insurance">
              <IfInsurancePage />
            </FeatureWrapper>
          </Route>

          <Route>
            <VisitTracker slug={slug} />
            <Page
              head={
                business?.billing_lock_reason === 'stripe_unpaid' ? (
                  <BillingLockBanner />
                ) : (
                  business?.form === BusinessForms.DEMO &&
                  !isBusinessBillingLocked(business) && <DemoBanner />
                )
              }
            >
              <AnimatedContentLoader isLoading={false}>
                {slug && (
                  <Switch>
                    <Redirect from={`${baseUrl}/get-started`} to={getDashboardPageUrl(slug)} />

                    <Route path={`${baseUrl}/dashboard`}>
                      <DashboardPage />
                    </Route>

                    <Route exact path={`${baseUrl}`}>
                      <BusinessHomeRoute slug={slug} />
                    </Route>

                    <Route path={`${baseUrl}/travel-and-expenses/:claimId/trips/new`}>
                      <ExpenseClaimsRoute>
                        <TripEditorPage />
                      </ExpenseClaimsRoute>
                    </Route>

                    <Route path={`${baseUrl}/travel-and-expenses/:claimId/trips/:tripId`}>
                      <ExpenseClaimsRoute>
                        <TripEditorPage />
                      </ExpenseClaimsRoute>
                    </Route>

                    <Route path={`${baseUrl}/travel-and-expenses/:claimId/expenses/new`}>
                      <ExpenseClaimsRoute>
                        <ExpenseEditorPage />
                      </ExpenseClaimsRoute>
                    </Route>

                    <Route path={`${baseUrl}/travel-and-expenses/:claimId/expenses/:expenseId`}>
                      <ExpenseClaimsRoute>
                        <ExpenseEditorPage />
                      </ExpenseClaimsRoute>
                    </Route>

                    <Route path={`${baseUrl}/travel-and-expenses/:claimId`}>
                      <ExpenseClaimsRoute>
                        <ClaimSummaryPage />
                      </ExpenseClaimsRoute>
                    </Route>

                    <Route exact path={`${baseUrl}/travel-and-expenses`}>
                      <ExpenseClaimsRoute>
                        <TravelAndExpensesPage />
                      </ExpenseClaimsRoute>
                    </Route>

                    <Route path={`${baseUrl}/documents`}>
                      <PermissionBoundary required={PermissionSet.BOOKKEEPING_VIEW}>
                        <DocumentPage location={baseUrl} />
                      </PermissionBoundary>
                    </Route>

                    <Route path={`${baseUrl}/invoicing`}>
                      <FeatureWrapper feature="invoicing">
                        <PermissionBoundary required={PermissionSet.INVOICING_VIEW}>
                          <InvoicingPage />
                        </PermissionBoundary>
                      </FeatureWrapper>
                    </Route>

                    <Route path={`${baseUrl}/products`}>
                      <FeatureWrapper feature="invoicing">
                        <PermissionBoundary required={PermissionSet.INVOICING_VIEW}>
                          <ProductPage />
                        </PermissionBoundary>
                      </FeatureWrapper>
                    </Route>

                    <Route path={`${baseUrl}/accounts`}>
                      <PermissionBoundary required={PermissionSet.ACCOUNTS_VIEW}>
                        <AccountsPage />
                      </PermissionBoundary>
                    </Route>

                    <Route path={`${baseUrl}/accounting`}>
                      <PermissionBoundary required={PermissionSet.REPORTING_VIEW}>
                        <PeriodPage />
                      </PermissionBoundary>
                    </Route>

                    <Route path={`${baseUrl}/vat`}>
                      <PermissionBoundary required={PermissionSet.REPORTING_VIEW}>
                        <VatPage />
                      </PermissionBoundary>
                    </Route>

                    <Route path={`${baseUrl}/reporting`}>
                      <PermissionBoundary required={PermissionSet.REPORTING_VIEW}>
                        <ReportsPage />
                      </PermissionBoundary>
                    </Route>

                    <Route path={`${baseUrl}/files`}>
                      <PermissionBoundary required={PermissionSet.FILES_VIEW}>
                        <FilesPage />
                      </PermissionBoundary>
                    </Route>

                    <Route path={`${baseUrl}/workflows`}>
                      <FlagWrapper flag="workflows">
                        <PermissionBoundary required={PermissionSet.WORKFLOWS_HUB}>
                          <WorkflowsPage />
                        </PermissionBoundary>
                      </FlagWrapper>
                    </Route>

                    {/* Luca settings now lives inside the settings page (DEV-2239); keep the old
                        top-level path working by deep-linking to the Luca section. */}
                    <Redirect
                      from={`${baseUrl}/luca-settings`}
                      to={getSettingsPageUrl(slug, { page: 'luca' })}
                    />

                    <Route path={`${baseUrl}/settings`}>
                      <SettingsPage />
                    </Route>

                    <Route path={`${baseUrl}/contacts`}>
                      <FeatureWrapper feature="invoicing">
                        <PermissionBoundary required={PermissionSet.CONTACT_VIEW}>
                          <ContactsPage />
                        </PermissionBoundary>
                      </FeatureWrapper>
                    </Route>
                  </Switch>
                )}
              </AnimatedContentLoader>
            </Page>
          </Route>
        </Switch>
      </BillingLockGuard>

      <ImportJobsTracker />
    </ImportJobProvider>
  )
}

const ExpenseClaimsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureWrapper feature="expense_claims">
    <FlagWrapper flag="expense_claims">
      <PermissionBoundary required={PermissionSet.EXPENSE_CLAIMS_VIEW}>
        {children}
      </PermissionBoundary>
    </FlagWrapper>
  </FeatureWrapper>
)

const BusinessHomeRoute: React.FC<{ slug: string }> = ({ slug }) => {
  const permissionsLoaded = usePermissionsLoaded()
  const canViewDashboard = usePermission(PermissionSet.DATE_RANGE_VIEW)
  const canViewInvoicing = usePermission(PermissionSet.INVOICING_VIEW)
  const canViewBookkeeping = usePermission(PermissionSet.BOOKKEEPING_VIEW)
  const canViewAccounts = usePermission(PermissionSet.ACCOUNTS_VIEW)
  const canViewFiles = usePermission(PermissionSet.FILES_VIEW)
  const canViewContacts = usePermission(PermissionSet.CONTACT_VIEW)
  const canViewExpenseClaims = usePermission(PermissionSet.EXPENSE_CLAIMS_VIEW)
  if (!permissionsLoaded || canViewDashboard) {
    return <DashboardPage />
  }

  return (
    <Redirect
      to={getBusinessHomePath(slug, {
        dashboard: false,
        bookkeeping: canViewBookkeeping,
        invoicing: canViewInvoicing,
        files: canViewFiles,
        contacts: canViewContacts,
        accounts: canViewAccounts,
        expenseClaims: canViewExpenseClaims
      })}
    />
  )
}

const VisitTracker: React.FC<{ slug: string }> = ({ slug }) => {
  const { data: business } = useBusinessContext()
  const isBillingLocked = isBusinessBillingLocked(business)
  const canTriggerBankBackgroundFetch = usePermission(PermissionSet.BOOKKEEPING_EDIT)

  // Business visit tracker
  useQuery({
    queryKey: ['visit_tracker', slug],
    queryFn: async () => {
      await trackBusinessPageVisit({ slug })
      return true
    },
    enabled: Boolean(slug) && !isBillingLocked,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false
  })

  // Bank integration background fetch
  useQuery({
    queryKey: ['background_fetch', slug],
    queryFn: async () => {
      await triggerBankIntegrationBackgroundFetch({ slug })
      return true
    },
    enabled: Boolean(slug) && !isBillingLocked && canTriggerBankBackgroundFetch,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 15, // 15 min
    refetchInterval: 1000 * 60 * 60, // 1 hour
    refetchIntervalInBackground: true,
    retry: false
  })

  return null
}
